import { useState } from 'react';
import { Trash2 } from 'lucide-react';

import { useUpdateTransferRecord } from '@/api/transfer/use-update-transfer-record.ts';
import type { CategoryDto, TransferRecordDto } from '@/api/types';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { formatPrice } from '@/helpers/formatters.ts';

type TransferRecordRowProps = {
  transferId: string;
  record: TransferRecordDto;
  categories?: CategoryDto[];
  onDelete?: (recordId: string) => void;
};

// Parent keys this by `${record.uuid}-${record.updatedAt}`, so a remote update
// remounts the row and resets local description state to the fresh value.
const TransferRecordRow = ({
  transferId,
  record,
  categories,
  onDelete,
}: TransferRecordRowProps) => {
  const { mutate: updateRecord, isPending } = useUpdateTransferRecord(transferId);

  const initialDescription = record.items.join(', ');
  const [description, setDescription] = useState(initialDescription);

  const isDirty = description !== initialDescription;

  const handleSaveDescription = () => {
    updateRecord({ recordId: record.uuid, description });
  };

  return (
    <div className="flex flex-col gap-2 border rounded-lg p-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 font-medium">
          {record.default ? 'Default' : 'Split'} — {formatPrice(record.amount)}
        </div>
        <select
          className="order-last h-7 w-full rounded-md border border-input bg-input/20 dark:bg-input/30 px-2 text-xs sm:order-none sm:w-auto"
          value={record.category?.uuid || ''}
          onChange={(event) =>
            event.target.value &&
            updateRecord({ recordId: record.uuid, categoryId: event.target.value })
          }
        >
          <option value="">Select category</option>
          {categories?.map((category) => (
            <option key={category.uuid} value={category.uuid}>
              {category.name}
            </option>
          ))}
        </select>
        {!record.default && onDelete && (
          <Button variant="outline" size="icon" onClick={() => onDelete(record.uuid)}>
            <Trash2 className="size-3.5" />
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Input
          className="min-w-0 flex-1"
          placeholder="Description (comma separated items)"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        {isDirty && (
          <Button size="sm" disabled={isPending} onClick={handleSaveDescription}>
            Save
          </Button>
        )}
      </div>
    </div>
  );
};

export default TransferRecordRow;
